if (!customElements.get('media-gallery')) {
  customElements.define(
    'media-gallery',
    class MediaGallery extends HTMLElement {
      constructor() {
        super();

        this.selectors = {
          viewer: '[id^="GalleryViewer"]',
          thumbnails: '[id^="GalleryThumbnails"]',
          mediaList: '[id^="Slider-Gallery"]',
          mediaItems: ['.product__media-item'],
        };

        this.sliderInstance = false;
        this.thumbsInstance = false;
      }

      connectedCallback() {
        this.init();
      }

      init() {
        this.elements = window.FoxTheme.utils.queryDomNodes(this.selectors, this);
        this.mediaLayout = this.dataset.mediaLayout;
        this.onlyImage = this.dataset.onlyImage === 'true';
        this.enableDesktopSlider = this.dataset.enableDesktopSlider === 'true';
        this.enableMobileThumbnails = this.dataset.enableMobileThumbnails === 'true';
        this.enableImageZoom = this.dataset.enableImageZoom === 'true';
        this.mobileCarouselStyle = this.dataset.mobileCarouselStyle || 'default';
        this.context = this.dataset.context;
        this.setSliderOptions();

        const mql = window.matchMedia(FoxTheme.config.mediaQueryMobile);
        mql.onchange = this.updateMediaLayout.bind(this);
        this.updateMediaLayout();

        if (this.enableImageZoom) {
          this.initImageZoom();
        }
      }

      setSliderOptions() {
        const mediaItemGap = parseInt(this.dataset.mediaItemGap);
        const mediaItemGapMobile = parseInt(this.dataset.mediaItemGapMobile);
        const isPeekMode = this.mobileCarouselStyle === 'peek';
        const isCardsMode = this.mobileCarouselStyle === 'cards';
        const isSpecialMobile = isPeekMode || isCardsMode;

        let mobileSlidesPerView = this.enableMobileThumbnails ? 1 : 'auto';
        let mobileCentered = false;
        let mobileSpaceBetween = mediaItemGapMobile;
        let mobileLoop = this.mediaLayout === 'carousel-thumbnails' ? false : true;
        let mobilePaginationType = 'fraction';

        if (isPeekMode) {
          mobileSlidesPerView = 1.25;
          mobileCentered = true;
          mobileSpaceBetween = 8;
          mobileLoop = true;
          mobilePaginationType = 'bullets';
        } else if (isCardsMode) {
          mobileSlidesPerView = 1.3;
          mobileCentered = true;
          mobileSpaceBetween = 10;
          mobileLoop = true;
          mobilePaginationType = 'bullets';
        }

        this.sliderOptions = {
          init: false,
          slidesPerView: mobileSlidesPerView,
          centeredSlides: mobileCentered,
          spaceBetween: mobileSpaceBetween,
          loop: mobileLoop,

          grabCursor: true,
          allowTouchMove: true,
          autoHeight: false,
          speed: 350,
          cssMode: false,
          touchRatio: 1,
          longSwipesRatio: 0.3,
          shortSwipes: true,
          followFinger: true,
          resistance: true,
          resistanceRatio: 0.5,
          breakpoints: {
            768: {
              slidesPerView: 1,
              centeredSlides: false,
              spaceBetween: mediaItemGap,
              autoHeight: true,
            },
          },
          navigation: {
            nextEl: this.querySelector('.swiper-button-next'),
            prevEl: this.querySelector('.swiper-button-prev'),
          },
          pagination: this.mediaLayout === 'carousel-thumbnails' ? false : {
            el: this.querySelector('.swiper-pagination'),
            clickable: true,
            type: mobilePaginationType,
          },
          threshold: 2,
        };

        this.thumbsOptions = {
          slidesPerView: 4,
          breakpoints: {
            461: {
              slidesPerView: 5,
              direction: 'horizontal',
            },
          },
          spaceBetween: mediaItemGapMobile,
          loop: false,
          freeMode: true,
          watchSlidesProgress: true,
          threshold: 2,
          breakpoints: {
            768: {
              spaceBetween: mediaItemGap,
            },
          },
        };

        switch (this.mediaLayout) {
          case 'vertical-carousel':
          case 'thumbnails-left':
            this.thumbsOptions.breakpoints = {
              ...this.thumbsOptions.breakpoints,
              768: {
                direction: 'vertical',
                slidesPerView: 'auto',
                spaceBetween: mediaItemGap,
              },
            };
            break;
          case 'carousel':
            this.thumbsOptions.breakpoints = {
              ...this.thumbsOptions.breakpoints,
              1024: {
                slidesPerView: 5,
              },
              1536: {
                slidesPerView: 7,
              },
            };
            break;
          case 'carousel-thumbnails':
            this.thumbsOptions = null;
            break;
        }
      }

      updateMediaLayout() {
        if (FoxTheme.config.mqlMobile) {
          this.initSlider();
        } else {
          if (this.enableDesktopSlider) {
            this.initSlider();
          } else {
            this.destroySlider();
          }
        }
      }

      initSlider() {
        if (typeof this.sliderInstance !== 'object') {
          if ((this.enableDesktopSlider || this.enableMobileThumbnails) && this.elements.thumbnails && this.mediaLayout !== 'carousel-thumbnails') {
            this.thumbsInstance = new window.FoxTheme.Carousel(this.elements.thumbnails, this.thumbsOptions);
            this.thumbsInstance.init();

            this.sliderOptions.thumbs = {
              swiper: this.thumbsInstance.slider,
              autoScrollOffset: 2,
            };
          }

          // Cards mode: duplicate slides for smooth loop when few media
          if (this.mobileCarouselStyle === 'cards' && FoxTheme.config.mqlMobile) {
            const mediaList = this.querySelector('.product__media-list');
            const slides = mediaList ? mediaList.querySelectorAll('.product__media-item') : [];
            if (slides.length > 0 && slides.length < 3) {
              const needed = 3 - slides.length;
              for (let i = 0; i < needed; i++) {
                const clone = slides[i % slides.length].cloneNode(true);
                clone.removeAttribute('id');
                clone.classList.add('product__media-item--clone');
                mediaList.appendChild(clone);
              }
            }
          }

          this.sliderInstance = new window.FoxTheme.Carousel(this.elements.viewer, this.sliderOptions, [
            FoxTheme.Swiper.Thumbs,
          ]);
          this.sliderInstance.init();

          this.handleSliderAfterInit();
          this.handleSlideChange();

          this.sliderInstance.slider.init();

          if (this.mediaLayout === 'thumbnails-left') {
            this.initThumbnailsLeftOverflow();
          }

          if (this.mediaLayout === 'carousel-thumbnails') {
            const paginationEl = this.querySelector('.product__media-gallery-thumbnails-pagination');
            if (paginationEl && this.sliderInstance.slider) {
              const bullets = paginationEl.querySelectorAll('.swiper-pagination-bullet');
              
              // Fonction pour détecter si le scroll est nécessaire
              const checkScrollNeeded = () => {
                const needsScroll = paginationEl.scrollWidth > paginationEl.clientWidth;
                if (needsScroll) {
                  paginationEl.classList.add('has-scroll');
                } else {
                  paginationEl.classList.remove('has-scroll');
                }
              };
              
              // Vérifier au chargement et lors du redimensionnement
              checkScrollNeeded();
              const resizeObserver = new ResizeObserver(() => {
                checkScrollNeeded();
              });
              resizeObserver.observe(paginationEl);
              
              bullets.forEach((bullet, index) => {
                bullet.addEventListener('click', (e) => {
                  e.preventDefault();
                  e.stopPropagation();
                  this.sliderInstance.slider.slideTo(index, 300);
                });
                
                bullet.addEventListener('keydown', (e) => {
                  if (e.key === 'Enter' || e.key === ' ') {
                    e.preventDefault();
                    this.sliderInstance.slider.slideTo(index, 300);
                  }
                });
              });

              this.sliderInstance.slider.on('slideChange', () => {
                const activeIndex = this.sliderInstance.slider.activeIndex;
                bullets.forEach((bullet, index) => {
                  if (index === activeIndex) {
                    bullet.classList.add('swiper-pagination-bullet-active');
                    bullet.setAttribute('aria-current', 'true');
                    
                    // Scroller seulement si le scroll est nécessaire
                    const paginationContainer = paginationEl;
                    const needsScroll = paginationContainer.scrollWidth > paginationContainer.clientWidth;
                    
                    if (needsScroll) {
                      const bulletRect = bullet.getBoundingClientRect();
                      const containerRect = paginationContainer.getBoundingClientRect();
                      const bulletOffsetLeft = bullet.offsetLeft;
                      const padding = 16; // 1.6rem en pixels
                      
                      // Calculer la position pour placer le bullet à gauche avec un petit padding
                      const targetScrollLeft = bulletOffsetLeft - padding;
                      
                      // Vérifier si le bullet est déjà visible à gauche
                      const isVisibleLeft = bulletRect.left >= containerRect.left && bulletRect.left <= containerRect.right;
                      const isVisibleRight = bulletRect.right >= containerRect.left && bulletRect.right <= containerRect.right;
                      
                      // Si le bullet n'est pas complètement visible ou est trop à droite, scroller
                      if (!isVisibleLeft || (isVisibleRight && bulletRect.left > containerRect.left + padding)) {
                        paginationContainer.scrollTo({
                          left: Math.max(0, targetScrollLeft),
                          behavior: 'smooth'
                        });
                      }
                    }
                  } else {
                    bullet.classList.remove('swiper-pagination-bullet-active');
                    bullet.removeAttribute('aria-current');
                  }
                });
              });
            }
          }
        }
      }

      destroySlider() {
        if (typeof this.sliderInstance === 'object') {
          this.sliderInstance.slider.destroy();
          this.sliderInstance = false;
        }
      }

      initThumbsSlider() {
        if (typeof this.thumbsInstance !== 'object') {
          this.thumbsInstance = new window.FoxTheme.Carousel(this.selectors.thumbnails, this.thumbsOptions);
          this.thumbsInstance.init();
        }
      }

      initImageZoom() {
        let dataSource = [];
        const allMedia = [...this.querySelectorAll('.product__media-item:not(.swiper-slide-duplicate)')];
        if (allMedia) {
          allMedia.forEach((media) => {
            switch (media.dataset.mediaType) {
              case 'model':
                dataSource.push({
                  id: media.dataset.mediaIndex,
                  html: `<div class="pswp__item--${media.dataset.mediaType}">${
                    media.querySelector('product-model').outerHTML
                  }</div>`,
                  mediaType: media.dataset.mediaType,
                });
                break;
              case 'video':
              case 'external_video':
                dataSource.push({
                  id: media.dataset.mediaIndex,
                  html: `<div class="pswp__item--${media.dataset.mediaType}">${
                    media.querySelector('video-element').outerHTML
                  }</div>`,
                  mediaType: media.dataset.mediaType,
                });
                break;
              case 'image':
                dataSource.push({
                  id: media.dataset.mediaIndex,
                  src: media.dataset.src,
                  width: media.dataset.pswpWidth,
                  height: media.dataset.pswpHeight,
                  mediaType: media.dataset.mediaType,
                });
                break;
            }
          });
        }

        this.lightbox = new window.FoxTheme.PhotoSwipeLightbox({
          dataSource: dataSource,
          pswpModule: window.FoxTheme.PhotoSwipe,
          bgOpacity: 1,
          arrowPrev: false,
          arrowNext: false,
          zoom: false,
          close: false,
          counter: false,
          preloader: false,
        });

        this.lightbox.addFilter('thumbEl', (thumbEl, { id }, index) => {
          if (this.sliderInstance && this.sliderInstance.slider) {
            const { slides, activeIndex } = this.sliderInstance.slider;
            if (slides[activeIndex]) {
              const el = slides[activeIndex].querySelector('img');
              if (el) {
                return el;
              }
            }
          }

          return thumbEl;
        });

        this.lightbox.addFilter('placeholderSrc', (placeholderSrc, { data: { id } }) => {
          if (this.sliderInstance && this.sliderInstance.slider) {
            const { slides, activeIndex } = this.sliderInstance.slider;
            if (slides[activeIndex]) {
              const el = slides[activeIndex].querySelector('img');
              if (el) {
                return el.src;
              }
            }
          }

          return placeholderSrc;
        });

        this.lightbox.on('change', () => {
          window.pauseAllMedia(this);
          if (this.sliderInstance && this.sliderInstance.slider) {
            const { currIndex } = this.lightbox.pswp;
            const currentIndex = this.mediaLayout === 'carousel-thumbnails' 
              ? this.sliderInstance.slider.activeIndex 
              : this.sliderInstance.slider.realIndex;
            
            if (currentIndex !== currIndex) {
              if (this.mediaLayout === 'carousel-thumbnails') {
                this.sliderInstance.slider.slideTo(currIndex, 100);
              } else {
                this.sliderInstance.slider.slideToLoop(currIndex, 100, false);
              }
            }
          }
        });

        this.lightbox.on('pointerDown', (e) => {
          if (this.lightbox.pswp.currSlide.data.mediaType != 'image') {
            e.preventDefault();
          }
        });

        this.lightbox.on('uiRegister', () => {
          if (!this.onlyImage) {
            this.lightbox.pswp.ui.registerElement({
              name: 'next',
              ariaLabel: 'Next slide',
              order: 3,
              isButton: true,
              html: '<svg class="pswp-icon-next flip-x" viewBox="0 0 100 100"><path d="M 10,50 L 60,100 L 65,90 L 25,50  L 65,10 L 60,0 Z"></path></svg>',
              onClick: (event, el) => {
                this.lightbox.pswp.next();
              },
            });
            this.lightbox.pswp.ui.registerElement({
              name: 'prev',
              ariaLabel: 'Previous slide',
              order: 1,
              isButton: true,
              html: '<svg class="pswp-icon-prev rtl-flip-x" viewBox="0 0 100 100"><path d="M 10,50 L 60,100 L 65,90 L 25,50  L 65,10 L 60,0 Z"></path></svg>',
              onClick: (event, el) => {
                this.lightbox.pswp.prev();
              },
            });
          }
          this.lightbox.pswp.ui.registerElement({
            name: 'close-zoom',
            ariaLabel: 'Close zoom image',
            order: 2,
            isButton: true,
            html: '<svg xmlns="http://www.w3.org/2000/svg" aria-hidden="true" focusable="false" role="presentation" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><line x1="18" y1="6" x2="6" y2="18"></line><line x1="6" y1="6" x2="18" y2="18"></line></svg>',
            onClick: (event, el) => {
              this.lightbox.pswp.close();
            },
          });
        });

        this.lightbox.init();

        FoxTheme.utils.addEventDelegate({
          selector: '.js-photoswipe--zoom',
          context: this,
          handler: (e, media) => {
            if (media.dataset.mediaType === 'image') {
              const index = Number(media.dataset.mediaIndex) || 0;
              this.lightbox.loadAndOpen(index);
            }
          },
        });
      }

      initThumbnailsLeftOverflow() {
        const thumbsEl = this.querySelector('.product__gallery-tl-thumbs');
        if (!thumbsEl) return;

        const prevBtn = thumbsEl.querySelector('.product__gallery-tl-nav--prev');
        const nextBtn = thumbsEl.querySelector('.product__gallery-tl-nav--next');

        const checkOverflow = () => {
          if (!this.thumbsInstance || !this.thumbsInstance.slider) return;
          const swiper = this.thumbsInstance.slider;
          const virtualSize = swiper.virtualSize || 0;
          const containerHeight = thumbsEl.getBoundingClientRect().height;

          if (virtualSize > containerHeight + 5) {
            const translate = Math.abs(swiper.translate || 0);
            const maxTranslate = Math.abs(swiper.maxTranslate() || 0);
            const hasTop = translate > 5;
            const hasBottom = translate < maxTranslate - 5;
            thumbsEl.classList.toggle('has-overflow-top', hasTop);
            thumbsEl.classList.toggle('has-overflow-bottom', hasBottom);
          } else {
            thumbsEl.classList.remove('has-overflow-top', 'has-overflow-bottom');
          }
        };

        // Nav buttons
        if (prevBtn && this.thumbsInstance && this.thumbsInstance.slider) {
          prevBtn.addEventListener('click', () => {
            this.thumbsInstance.slider.slidePrev();
          });
        }
        if (nextBtn && this.thumbsInstance && this.thumbsInstance.slider) {
          nextBtn.addEventListener('click', () => {
            this.thumbsInstance.slider.slideNext();
          });
        }

        // Check on init
        setTimeout(checkOverflow, 500);

        // Check on thumbs slide/scroll
        if (this.thumbsInstance && this.thumbsInstance.slider) {
          this.thumbsInstance.slider.on('slideChange', checkOverflow);
          this.thumbsInstance.slider.on('transitionEnd', checkOverflow);
          this.thumbsInstance.slider.on('setTranslate', checkOverflow);
        }

        window.addEventListener('resize', checkOverflow);
      }

      handleSliderAfterInit() {
        this.sliderInstance.slider.on('afterInit', (swiper) => {
          const { slides, activeIndex } = swiper;

          if (slides[activeIndex]) {
            const isModelMediaType = slides[activeIndex].dataset.mediaType === 'model';
            this.toggleSliderDraggableState(!isModelMediaType);
          }
        });
      }

      handleSlideChange() {
        const eventName = this.mediaLayout === 'carousel-thumbnails' ? 'slideChange' : 'realIndexChange';
        
        this.sliderInstance.slider.on(eventName, (swiper) => {
          const { slides, activeIndex, thumbs } = swiper;
          const index = this.mediaLayout === 'carousel-thumbnails' ? activeIndex : swiper.realIndex;

          if (thumbs && thumbs.swiper) {
            thumbs.swiper.slideTo(index);
          }

          if (slides[activeIndex]) {
            this.playActiveMedia(slides[activeIndex]);

            const isModelMediaType = slides[activeIndex].dataset.mediaType === 'model';
            this.toggleSliderDraggableState(!isModelMediaType);
          }
        });
      }

      toggleSliderDraggableState(isDraggable) {
        if (this.sliderInstance.slider.allowTouchMove !== isDraggable) {
          this.sliderInstance.slider.allowTouchMove = isDraggable;
        }
      }

      playActiveMedia(selected) {
        const deferredMedia = selected.querySelector('product-model');
        if (deferredMedia) deferredMedia.loadContent(false);
      }

      setActiveMedia(variant) {
        if (!variant || !variant.hasOwnProperty('featured_media') || !variant.featured_media) return;

        if (this.sliderInstance && this.sliderInstance.slider) {
          const slideIndex = variant.featured_media.position || 0;
          const targetIndex = slideIndex - 1;
          
          if (this.mediaLayout === 'carousel-thumbnails') {
            this.sliderInstance.slider.slideTo(targetIndex, 300);
          } else {
            this.sliderInstance.slider.slideToLoop(targetIndex);
          }
        } else {
          this.sortMediaItems(variant);
        }
      }

      sortMediaItems(variant) {
        let newMedias = this.elements.mediaItems;

        newMedias.sort(function (a, b) {
          return a.dataset.mediaIndex - b.dataset.mediaIndex;
        });

        newMedias.some((media, index) => {
          if (media.dataset.mediaId == variant.featured_media.id) {
            const [element] = newMedias.splice(index, 1);
            newMedias.unshift(element);
            return true;
          }
        });

        this.elements.mediaList.innerHTML = '';
        newMedias.forEach((media) => {
          this.elements.mediaList.appendChild(media);
        });

        if (!FoxTheme.config.mqlMobile && this.context !== 'quickview') {
          const selectedMedia = this.querySelector(`[data-media-id="${variant.featured_media.id}"]`);
          if (selectedMedia) {
            window.scrollTo({ top: selectedMedia.offsetTop, behavior: 'smooth' });
          }
        }
      }
    }
  );
}
